import axios from "axios";
import qs from "qs";
import router from "@/router";
import aes from "@/utils/aes.js";
import rsa from "@/utils/rsa.js";
import { getToken } from "@/utils/auth";
import store from "@/store";

// --- 1. 定义公共常量 (只定义一次) ---
const BASE = "https://kxwj.cn";
// const BASE = 'http://192.168.31.148:8086';
const ROOT = `${BASE}/server`;

// 需要对【请求参数】进行加密的接口
const encryptionInterface = [
  "/server/connect/upd/v2",
  "/server/connect/add/v2",
  "/server/connect/del/v2",
  "/device/stat/stat/data/v2",
  "/device/stat/export/data/v2",
  "device/stat/user/account/export/data/v2",
  "connect/get/v2",
  "device_data/find/v2",
  "connect/page/v2",
];

// 需要对【响应结果】进行解密的接口 (查、导出)
const decryptionInterface = [
  "/server/device/stat/stat/data/v2",
  "/server/device/stat/stat/minutes/v2", // not in use
  "/server/device/stat/export/data/v2",
  "/server/device/stat/export/org/data/v2", // not in use
  "/server/device/stat/user/account/export/data/v2",
  "/server/device/data/max/getByDevice/v2", // not in use
  "/server/connect/get/v2",
  "/server/connect/getCfDevice/v2", // not in use
  "/server/device_data/find/v2",
  "/server/device_data/newest/v2",
  "/server/device_data/lastData/v2", // not in use
  "/server/connect/page/v2",
  "/server/connect/getRecentlyDevice/v2", // not in use
  "/server/connect/list/v2", // not in use
  "/server/connect/getDeviceConnect/v2",
  "/server/connect/getDeviceData/v2",
  "/server/connect/getNewDeviceData/v2",
  "/server/connect/getDeviceLastDataById/v2",
  "/server/connect/list/by/userName/v2",
  "/server/connect/getByDevice/v2",
  "/server/connect/getDeviceList/v2", //not in use
  "/server/statDay/list/v2",
];

// --- 2. 拦截器函数 ---

const requestInterceptorSuccess = (config) => {
  const stateStr = window.localStorage.getItem("kxwj_vuex_state");
  if (stateStr) {
    try {
      const data = JSON.parse(stateStr);
      if (data.expire && Date.now() > data.expire) {
        store.dispatch("LogOut").then(() => {
          router.push({
            name: "login",
            query: { redirect: router.history.current.path },
          });
        });
        // 阻断请求
        return Promise.reject(new Error("FRONTEND_TIMEOUT"));
      } else if (data.expire) {
        data.expire = Date.now() + 60 * 60 * 1000;
        window.localStorage.setItem("kxwj_vuex_state", JSON.stringify(data));
      }
    } catch (e) {
      console.error("解析 Vuex 状态失败:", e);
    }
  }
  let token = getToken();

  config.headers["X-Requested-With"] = "XMLHttpRequest";
  if (token) {
    config.headers.Authorization = "Bearer " + token;
  }

  // 默认的 GET 请求序列化处理 (保留你原有的逻辑习惯)
  if (config.method === "get" && config.params) {
    let url = config.url + "?";
    for (const propName of Object.keys(config.params)) {
      const value = config.params[propName];
      var part = encodeURIComponent(propName) + "=";
      if (value !== null && typeof value !== "undefined") {
        if (typeof value === "object") {
          for (const key of Object.keys(value)) {
            let params = propName + "[" + key + "]";
            var subPart = encodeURIComponent(params) + "=";
            url += subPart + encodeURIComponent(value[key]) + "&";
          }
        } else {
          url += part + encodeURIComponent(value) + "&";
        }
      }
    }
    url = url.slice(0, -1);
    config.params = {};
    config.url = url;
  }

  // ==========================================
  // 【核心】加解密逻辑处理 START
  // ==========================================
  // 获取纯净的请求路径
  const requestUrl = config.url || "";
  const fullUrl = config.baseURL ? config.baseURL + requestUrl : requestUrl;
  const pathIndex = fullUrl.indexOf("/server/");
  let requestPath = pathIndex !== -1 ? fullUrl.substring(pathIndex) : fullUrl;
  requestPath = requestPath.split("?")[0]; // 去除 GET 请求带的参数

  const isEncryptReq = encryptionInterface.some((api) =>
    requestPath.includes(api),
  );
  const isDecryptRes = decryptionInterface.some((api) =>
    requestPath.includes(api),
  );

  // 1. 【通用处理】过滤掉 undefined 的字段
  if (
    config.data &&
    Object.prototype.toString.call(config.data) === "[object Object]"
  ) {
    config.data = Object.fromEntries(
      Object.entries(config.data).filter(([_, value]) => value !== undefined), //eslint-disable-line
    );
  }

  // 2. 【秘钥生成】只要命中请求加密或响应解密中任意一项
  if (isEncryptReq || isDecryptRes) {
    const aesKey = aes.generateRandomAESKey(16);

    // RSA 加密 AES 秘钥放入请求头
    config.headers["X-Secret-Key"] = rsa.encrypt(aesKey);
    // 从本地存储获取客户端 RSA-ID (如果在 vuex 中，请对应调整)
    config.headers["X-Secret-Client"] = localStorage.getItem("rsaId") || "";

    // 🔥 将秘钥和解密标识挂载到 config 上，方便响应拦截器取用
    config.customAesKey = aesKey;
    config.isDecryptRes = isDecryptRes;

    // 3. 【请求加密】
    if (isEncryptReq && config.data) {
      config.data = aes.encrypt(aesKey, JSON.stringify(config.data));

      // 【关键修复 2】：强制清理 method 级别的 Content-Type，确保应用 application/json
      // 如果后端收到 application/x-www-form-urlencoded，Tomcat 会把流提前消费掉，导致你的解密拦截器读到空对象
      config.headers["Content-Type"] = "application/json";
      if (config.headers.post) delete config.headers.post["Content-Type"];
      if (config.headers.put) delete config.headers.put["Content-Type"];
      if (config.headers.patch) delete config.headers.patch["Content-Type"];
    }
  }
  // ==========================================
  // 【核心】加解密逻辑处理 END
  // ==========================================

  return config;
};

const requestInterceptorError = (error) => {
  return Promise.reject(error);
};

const responseInterceptorSuccess = (response) => {
  const config = response.config;

  // ==========================================
  // 【核心】响应解密逻辑 START
  // ==========================================
  // 判断此接口是否被标记为需要解密，且状态正常
  const isEncrypted =
    response.headers &&
    (response.headers["x-data-encrypted"] === "true" ||
      response.headers["x-data-encrypted"] === true);

  // 判断后端是否明确标记了该响应已被加密，且前端成功保留了发请求时的解密钥匙
  if (isEncrypted && config && config.customAesKey && response.status === 200) {
    try {
      // 【增强防御】：确保返回的 data 确实是有内容的字符串再进行解密
      if (typeof response.data === "string" && response.data.trim() !== "") {
        // 对返回的密文进行 AES 解密，并重新赋值给 response.data
        const decryptedStr = aes.decrypt(config.customAesKey, response.data);
        response.data = JSON.parse(decryptedStr);
      }
    } catch (error) {
      console.error("响应数据解密/解析失败:", error);
      return Promise.reject({ message: "数据安全解析异常" });
    }
  }
  // ==========================================
  // 【核心】响应解密逻辑 END
  // ==========================================

  // (保留原有逻辑) 此处原本直接返回 response.data，你可以按需扩充业务错误码判断
  return response.data;
};

const responseInterceptorError = (error) => {
  if (error.response && error.response.status === 401) {
    store.dispatch("LogOut").then(() => {
      router.push({
        name: "login",
        query: router.history.current.path,
      });
    });
  }
  return Promise.reject(error);
};

// --- 3. 创建两个不同配置的实例 ---

/**
 * 实例1: jsonInstance
 * 用于 'application/json' 格式的请求
 */
const jsonInstance = axios.create({
  baseURL: ROOT,
});

/** 实例2: formInstance
 * 用于 'application/x-www-form-urlencoded' 格式的请求
 */
const formInstance = axios.create({
  baseURL: ROOT,
  transformRequest: [
    function (data) {
      // 使用 qs 序列化数据
      return qs.stringify(data);
    },
  ],
  responseType: "json",
});

// --- 4. 为两个实例应用相同的拦截器 ---

// 为 jsonInstance 应用拦截器
jsonInstance.interceptors.request.use(
  requestInterceptorSuccess,
  requestInterceptorError,
);
jsonInstance.interceptors.response.use(
  responseInterceptorSuccess,
  responseInterceptorError,
);

// 为 formInstance 应用相同的拦截器
formInstance.interceptors.request.use(
  requestInterceptorSuccess,
  requestInterceptorError,
);
formInstance.interceptors.response.use(
  responseInterceptorSuccess,
  responseInterceptorError,
);

/**
 * ==========================================
 * 项目初始化时调用：获取 RSA 公钥并存入本地
 * ==========================================
 */
const initRsaPublicKey = async () => {
  try {
    // 发起 GET 请求获取公钥
    const res = await jsonInstance.get("/getPublicKey");

    const data = res.data || res;
    const publicKey = data.rsaPublicKey;
    const rsaId = data.rsaId;

    if (publicKey) {
      localStorage.setItem("rsaPublicKey", publicKey);
    }
    if (rsaId) {
      localStorage.setItem("rsaId", rsaId);
    }
  } catch (error) {
    console.error("RSA公钥初始化失败:", error);
  }
};
export default formInstance;
export { jsonInstance as service, BASE, ROOT, initRsaPublicKey };
